import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Loader2, Shield, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const BG_IMG =
  "https://images.unsplash.com/photo-1774582388622-674315229274?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const LOGO =
  "https://images.squarespace-cdn.com/content/v1/65ea2001529ccd1a677ed52f/3543688a-b7e0-4677-901b-3c9d2aa2b063/LogoL30M-bleu-nuit.png";

export default function Login() {
  const { loginAdmin, loginClient, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("client"); // client | admin
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // login | forgot
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post(view === "code" ? "/auth/forgot-code" : "/auth/forgot-password", { email: forgotEmail });
    } catch { /* réponse identique dans tous les cas (anti-énumération) */ }
    finally { setForgotSent(true); setForgotBusy(false); }
  };

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/espace-client", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = mode === "admin"
        ? await loginAdmin(email, password)
        : await loginClient(code, email, password);
      navigate(u.role === "admin" ? "/admin" : "/espace-client", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Connexion impossible.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ink text-[#F3F4F6] font-body grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 to-ink" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="inline-block" data-testid="login-brand">
            <div className="bg-white rounded-xl px-6 py-4 w-fit shadow-lg">
              <img src={LOGO} alt="Le 30 du mois" className="h-16 w-auto object-contain" />
            </div>
          </Link>
          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-4">Espace sécurisé</p>
            <h2 className="font-serif text-4xl leading-tight max-w-sm">Vos payes, vos documents, en toute confidentialité.</h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#A0B2CE] hover:text-gold transition-colors mb-10" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>

          {/* Mode toggle */}
          <div className="flex gap-1 p-1 rounded-full border border-white/10 bg-white/[0.02] mb-10 w-fit">
            {[{ k: "client", label: "Client", icon: Lock }, { k: "admin", label: "Administration", icon: Shield }].map((m) => (
              <button
                key={m.k}
                onClick={() => { setMode(m.k); setError(""); }}
                data-testid={`mode-${m.k}`}
                className={`flex items-center gap-2 px-5 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                  mode === m.k ? "bg-gold text-ink" : "text-[#A0B2CE] hover:text-gold"
                }`}
              >
                <m.icon className="w-3.5 h-3.5" /> {m.label}
              </button>
            ))}
          </div>

          <h1 className="font-serif text-4xl mb-3">{view === "forgot" ? "Mot de passe oublié" : view === "code" ? "Code oublié" : mode === "admin" ? "Administration" : "Connexion client"}</h1>
          <p className="text-[#A0B2CE] mb-8">
            {view === "forgot"
              ? "Saisissez votre email : nous vous enverrons un lien pour choisir un nouveau mot de passe."
              : view === "code"
                ? "Saisissez votre email : nous vous renverrons votre code d'accès à 4 caractères."
                : mode === "admin" ? "Accédez à la gestion des clients." : "Votre email et votre mot de passe suffisent — le code ne sert que si votre adresse email est partagée entre plusieurs comptes."}
          </p>

          {view === "forgot" || view === "code" ? (
            <div data-testid="forgot-panel">
              {forgotSent ? (
                <div className="space-y-8">
                  <p className="text-[#A0B2CE]" data-testid="forgot-success">
                    {view === "code"
                      ? "Si un compte correspond à cet email, votre code d'accès vient d'être envoyé."
                      : "Si un compte correspond à cet email, un lien de réinitialisation vient d'être envoyé. Il est valable 1 heure."}
                  </p>
                  <button onClick={() => { setView("login"); setForgotSent(false); setForgotEmail(""); }} data-testid="forgot-back"
                    className="font-mono text-[11px] uppercase tracking-[0.12em] text-gold hover:text-[#B5952F] transition-colors">
                    ← Retour à la connexion
                  </button>
                </div>
              ) : (
                <form onSubmit={submitForgot} className="space-y-6" data-testid="forgot-form">
                  <div>
                    <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Votre email</label>
                    <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} data-testid="forgot-email"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300" />
                  </div>
                  <button type="submit" disabled={forgotBusy} data-testid="forgot-submit"
                    className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50">
                    {forgotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : view === "code" ? "Recevoir mon code" : "Recevoir le lien"}
                  </button>
                  <button type="button" onClick={() => setView("login")} data-testid="forgot-cancel"
                    className="w-full font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors">
                    ← Retour à la connexion
                  </button>
                </form>
              )}
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="login-form">
            {mode === "client" && (
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">
                  Code à 4 caractères <span className="text-[#5A6B8C]">— optionnel</span>
                </label>
                <input type="text" maxLength={4} value={code} onChange={(e) => setCode(e.target.value)} data-testid="login-code"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg tracking-[0.4em] uppercase transition-colors duration-300" />
              </div>
            )}
            <div>
              <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300" />
            </div>
            <div>
              <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Mot de passe</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 pr-10 text-lg transition-colors duration-300" />
                <button type="button" onClick={() => setShowPassword((v) => !v)} data-testid="toggle-password"
                  title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#A0B2CE] hover:text-gold transition-colors">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && <p className="text-red-400 text-sm font-mono" data-testid="login-error">{error}</p>}

            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors duration-300 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
            </button>
          </form>
          )}

          {view === "login" && mode === "client" && (
            <div className="mt-6 flex items-center gap-6">
              <button onClick={() => { setView("forgot"); setForgotSent(false); setForgotEmail(""); }} data-testid="forgot-password-link"
                className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors">
                Mot de passe oublié ?
              </button>
              <button onClick={() => { setView("code"); setForgotSent(false); setForgotEmail(""); }} data-testid="forgot-code-link"
                className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors">
                Code oublié ?
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
